/**
 * Shared presentation helpers for the Notes (Memo) list table columns.
 * Column behavior is implemented in MemoList cells; keep tokens here to stay DRY.
 */

/** Row / legend dot colors: audio, photos, video attachments, and comment indicator */
export const MEMO_FILE_TYPE_COLORS = {
  audio: '#22c55e',
  photo: '#3b82f6',
  video: '#f97316',
  comment: '#a855f7',
} as const;

export type MemoFileTypeColorKey = keyof typeof MEMO_FILE_TYPE_COLORS;

/** Legend order and copy (keys align with `MEMO_FILE_TYPE_COLORS`) */
export const MEMO_LEGEND_FILE_TYPE_ITEMS: ReadonlyArray<{
  key: MemoFileTypeColorKey;
  label: string;
}> = [
  { key: 'audio', label: 'Audio Files' },
  { key: 'photo', label: 'Photo Files' },
  { key: 'comment', label: 'Comments' },
  { key: 'video', label: 'Video Files' },
];

/**
 * Status pill colours by memo status id (1 Pending, 2 Under Review, 3 Processed), as token tint
 * pairs. None of these is a danger state, so none of them is red.
 */
export const memoStatusPillClass = (statusId: number) => {
  switch (statusId) {
    case 2:
      return 'bg-info text-info-foreground';
    case 3:
      return 'bg-success text-success-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

/** Matches Category (index 2) and Assigned (index 7) pill treatment */
export const MEMO_NOTES_CATEGORY_PILL_CLASS =
  'inline-flex max-w-full items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800';

/** Notes table row action icon buttons: no border; hover bg #dbeafe, icon #2563eb (inherits via text color) */
export const MEMO_ACTIONS_ICON_BUTTON_CLASS =
  'border-0 bg-transparent shadow-none text-muted-foreground hover:bg-[#dbeafe] hover:text-[#2563eb]';

/**
 * `Memo` only exposes `propertyAddressSearch`; split on commas into street vs city/state/zip when possible.
 */
export function splitPropertyAddressForMemoCell(address: string): { line1: string; line2: string } {
  const trimmed = address.trim();
  if (!trimmed) {
    return { line1: '', line2: '' };
  }
  const parts = trimmed
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length <= 1) {
    return { line1: parts[0] ?? '', line2: '' };
  }
  return {
    line1: parts[0] ?? '',
    line2: parts.slice(1).join(', '),
  };
}

/**
 * Parses a single-line US-style address ending in `City ST 12345` (no commas).
 * Works backward: ZIP → 2-letter state → last word before state = city (multi-word cities are not split).
 */
export function parseUsPropertyAddressSingleLine(address: string): {
  street: string;
  city: string;
  state: string;
  zip: string;
} | null {
  const trimmed = address.trim();
  if (!trimmed) {
    return null;
  }

  const zipMatch = trimmed.match(/\b(\d{5})(-\d{4})?\s*$/);
  if (!zipMatch) {
    return null;
  }
  const zip = zipMatch[1] + (zipMatch[2] ?? '');
  let rest = trimmed.slice(0, zipMatch.index).trimEnd();

  const stateMatch = rest.match(/\b([A-Z]{2})\s*$/i);
  if (!stateMatch) {
    return null;
  }
  const state = stateMatch[1].toUpperCase();
  rest = rest.slice(0, stateMatch.index).trimEnd();

  const words = rest.split(/\s+/).filter(Boolean);
  if (words.length < 2) {
    return null;
  }

  const city = words[words.length - 1] ?? '';
  const street = words.slice(0, -1).join(' ');

  return { street, city, state, zip };
}

export type MemoPropertyAddressLines = {
  primaryLine: string;
  secondaryLine: string | null;
  /** Full string for `title` / accessibility */
  full: string;
};

/**
 * Prefer comma-split (`line1` / `line2`); otherwise US single-line parse.
 */
export function formatMemoPropertyAddressForCell(address: string): MemoPropertyAddressLines {
  const trimmed = address.trim();
  if (!trimmed) {
    return { primaryLine: 'No Address', secondaryLine: null, full: '' };
  }

  const { line1, line2 } = splitPropertyAddressForMemoCell(trimmed);
  if (line2) {
    const primaryLine = line1 || trimmed;
    return {
      primaryLine,
      secondaryLine: line2,
      full: `${primaryLine}, ${line2}`,
    };
  }

  const parsed = parseUsPropertyAddressSingleLine(trimmed);
  if (parsed) {
    const secondaryLine = `${parsed.city}, ${parsed.state} ${parsed.zip}`;
    return {
      primaryLine: parsed.street,
      secondaryLine,
      full: `${parsed.street}, ${secondaryLine}`,
    };
  }

  const primaryLine = line1 || trimmed;
  return { primaryLine, secondaryLine: null, full: primaryLine };
}
